import { createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import axiosInstance from '../../utils/axios';
import amplifyConfig from '../../config';
import fileDownload from 'js-file-download';
import { BUSINESS_TYPE } from '../../constants';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../store';
import type { BusinessType, ErrorResponse, SteelCategory } from '../../types/common';
import type { MillSheetGridData, MillSheetSearchResponse, MillSheetSearchState, MillSheetType } from '../../types/millSheetSearch';

const fullTextSearchEndpoint = '/millsheet/fullTextSearch';
const fullTextSearchParameterName = '?q=';
const pdfDownloadEndpoint = '/millsheet/download-files';
const pdfDownloadParameterName = '?ids=';

const initialState: MillSheetSearchState = {
  isLoading: false,
  isDownloading: false,
  hasError: false,
  serverMessage: undefined,
  selectedBusinessType: BUSINESS_TYPE.DOMESTIC,
  selectedSteelCategory: null,
  selectedOrganizationId: null,
  selectedRows: [],
  searchResults: [],
};

const slice = createSlice({
  name: 'MillSheetSearch',
  initialState,
  reducers: {
    startLoading(state: MillSheetSearchState) {
      state.isLoading = true;
    },

    startDownloading(state: MillSheetSearchState) {
      state.isDownloading = true;
    },

    hasError(state: MillSheetSearchState, action: PayloadAction<string>) {
      // console.log(action.payload.response);
      const message = action.payload;
      state.isLoading = false;
      state.isDownloading = false;
      state.hasError = true;
      state.serverMessage = message;
    },

    resetMessage(state: MillSheetSearchState) {
      state.isLoading = false;
      state.serverMessage = undefined;
      state.hasError = false;
    },

    setServerMessage(state: MillSheetSearchState, action: PayloadAction<string>): void {
      state.isLoading = false;
      state.serverMessage = action.payload;
    },

    selectBusinessType(state: MillSheetSearchState, action: PayloadAction<BusinessType>): void {
      state.selectedBusinessType = action.payload;
      state.searchResults = [];
    },

    selectSteelCategory(state: MillSheetSearchState, action: PayloadAction<SteelCategory | undefined>): void {
      state.selectedSteelCategory = action.payload ?? null;
    },

    setOrganizationId(state: MillSheetSearchState, action: PayloadAction<number>): void {
      state.selectedOrganizationId = action.payload;
    },

    selectRows(state: MillSheetSearchState, action: PayloadAction<MillSheetType[]>): void {
      state.selectedRows = action.payload;
    },

    searchMillsheetSuccess(state: MillSheetSearchState, action: PayloadAction<MillSheetSearchResponse>): void {
      state.isLoading = false;
      const response: MillSheetSearchResponse = action.payload;
      // 2000件を超えたらメッセージ表示
      const totalValue: number = response.total.value;
      if (totalValue > 2000) {
        const message: string = response.total.value + '件見つかりましたが表示最大数（2000件）を超えています。検索条件を絞り込んで下さい。';
        state.serverMessage = message;
      }
      // グリッドデータ
      // console.log(response.hits);
      const rowData: MillSheetGridData[] = response.hits.map((item) => ({ _id: item._id, ...item._source }));
      state.searchResults = rowData;
    },

    downloadSuccess(state: MillSheetSearchState): void {
      state.isDownloading = false;
    },
  },
});

export const { reducer, actions } = slice;

const asyncErrorHandler = (err, dispatch) => {
  if (typeof err === 'string') {
    dispatch(slice.actions.hasError(err));
  } else if (err.isAxiosError) {
    // err: AxiosError
    if (err.response) {
      const serverResponse: ErrorResponse = err.response.data;
      dispatch(slice.actions.hasError(serverResponse.message));
    } else {
      dispatch(slice.actions.hasError(err.message));
    }
  } else {
    // err: Error
    dispatch(slice.actions.hasError(err.message));
  }
};

export const resetMessage =
  (): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  };

export const showErrorMessage =
  (message: string): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.hasError(message));
  };

export const selectBusinessType =
  (type: BusinessType): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.selectBusinessType(type));
  };

export const selectSteelCategory =
  (category: SteelCategory): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.selectSteelCategory(category));
  };

export const setOrganizationId =
  (organizationId: number): AppThunk =>
  (dispatch): void => {
    // console.log(organizationId);
    dispatch(slice.actions.setOrganizationId(organizationId));
  };

export const selectRows =
  (rows: MillSheetType[]): AppThunk =>
  (dispatch): void => {
    dispatch(slice.actions.selectRows(rows));
  };

export const searchMillSheet =
  (query: string, organizationIds: string, isAdminOrPoweruser: boolean): AppThunk =>
  async (dispatch): Promise<void> => {
    // console.log(params);
    dispatch(slice.actions.startLoading());

    // encodeURIComponentを使う
    const encodedQuery: string = encodeURIComponent(query);
    // adminでかつなりすまし組織が選択されている場合はクエリに付け加える
    const restrictByOrganization: string = isAdminOrPoweruser && organizationIds !== 'null' ? '&organizationIds=' + organizationIds : '';
    // console.log(restrictByOrganization);
    // DBからデータを取得
    await axiosInstance
      .get(amplifyConfig.apiGateway.URL + fullTextSearchEndpoint + fullTextSearchParameterName + encodedQuery + restrictByOrganization)
      .then((response) => {
        const result = response.data;
        dispatch(slice.actions.searchMillsheetSuccess(result));
      })
      .catch((err) => {
        // catchでエラーが返されたときはalertする
        asyncErrorHandler(err, dispatch);
      });
  };

export const downloadMillSheet =
  (ids: string[]): AppThunk =>
  async (dispatch): Promise<void> => {
    // console.log(params);
    dispatch(slice.actions.startDownloading());

    // query作成
    await axiosInstance
      .post(
        amplifyConfig.apiGateway.URL + pdfDownloadEndpoint,
        { ids },
        {
          responseType: 'blob',
          timeout: 1800000,
        },
      )
      .then((response) => {
        const contentDisposition = response.headers['content-disposition'];
        // dispositionは下記のようになっている：
        // attachment; filename="ML_0_200518_150009_JD1390005901_SA_E1_01_H001_S48_50_0N2259.PDF"
        const fileName = contentDisposition.split('attachment; filename="').join('').split('"').join('');
        // console.log(contentDisposition, fileName);
        fileDownload(response.data, fileName);
        dispatch(slice.actions.downloadSuccess());
      })
      .catch((err) => {
        // エラー表示
        // サーバからのjson responseをblobとして読もうとしてしまうため
        // FileReaderでblob→text変換してから利用する
        // https://jun711.github.io/aws/aws-amplify-handle-blob-response-and-json-error-response/
        // https://developer.mozilla.org/ja/docs/Web/API/FileReader
        // https://stackoverflow.com/questions/34495796/javascript-promises-with-filereader
        // https://qiita.com/opengl-8080/items/fbbc6b7995584d207740

        return new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onloadend = function (e) {
            const errorRes = e.currentTarget['result'];
            const errorText = JSON.parse(errorRes);
            if (errorText) {
              if (errorText.message) {
                errorText.message.message ? reject(errorText.message.message) : reject(errorText.message);
              } else if (errorText.error) {
                errorText.error.message ? reject(errorText.error.message) : reject(errorText.error);
              } else {
                reject(errorText);
              }
            }
          };
          if (err && err.response && err.response.data) {
            try {
              fr.readAsText(err.response.data);
            } catch (err) {
              reject(err);
            }
          }
        });
      })
      .catch((err) => {
        console.log(err);
        // catchでエラーが返されたときはalertする
        asyncErrorHandler(err, dispatch);
      });
  };

export default slice;
